<template>
  <div
    class="min-h-screen bg-gradient-to-br from-gray-50 via-white to-logo-color/5 relative overflow-hidden py-24"
  >
    <!-- Decorative Background Elements -->
    <div class="absolute inset-0 overflow-hidden pointer-events-none">
      <div
        class="absolute top-20 right-10 w-72 h-72 bg-logo-color/10 rounded-full blur-3xl"
      ></div>
      <div
        class="absolute bottom-20 left-10 w-96 h-96 bg-logo-color/5 rounded-full blur-3xl"
      ></div>
      <div
        class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-logo-color/3 rounded-full blur-3xl"
      ></div>
      <!-- Floating shapes -->
      <div
        class="absolute top-32 left-20 w-4 h-4 bg-logo-color/20 rounded-full animate-float"
      ></div>
      <div
        class="absolute top-48 right-32 w-6 h-6 bg-logo-color/15 rounded-full animate-float-delayed"
      ></div>
      <div
        class="absolute bottom-40 left-1/4 w-3 h-3 bg-logo-color/25 rounded-full animate-float"
      ></div>
    </div>

    <div class="container mx-auto px-4 relative z-10">
      <!-- Header Section -->
      <div
        class="text-center max-w-3xl mx-auto mb-20"
        data-aos="fade-up"
        data-aos-duration="800"
      >
        <span
          class="inline-block px-5 py-2 bg-logo-color/10 text-logo-color font-semibold tracking-wider uppercase text-xs rounded-full mb-8"
        >
          Get In Touch
        </span>
        <h1
          class="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight"
        >
          We'd Love to
          <span class="text-logo-color relative">
            Hear From You
            <svg
              class="absolute -bottom-2 left-0 w-full"
              height="8"
              viewBox="0 0 200 8"
              fill="none"
            >
              <path
                d="M1 5.5C47.6667 2.16667 141.4 -2.1 199 5.5"
                stroke="currentColor"
                stroke-width="3"
                stroke-linecap="round"
                class="text-logo-color/30"
              />
            </svg>
          </span>
        </h1>
        <p class="text-lg text-gray-600 leading-relaxed max-w-2xl mx-auto">
          Have questions or want to get involved? Reach out to us and let's
          start a meaningful conversation about making a difference.
        </p>
      </div>

      <div class="grid lg:grid-cols-5 gap-8 max-w-7xl mx-auto">
        <!-- Left Column - Contact Info -->
        <div
          class="lg:col-span-2 space-y-6"
          data-aos="fade-right"
          data-aos-duration="1000"
        >
          <!-- Main Contact Card -->
          <div
            class="bg-logo-color rounded-3xl p-8 text-white relative overflow-hidden shadow-[0_20px_60px_-10px_rgba(0,0,0,0.3)] shadow-logo-color/50"
          >
            <div
              class="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2"
            ></div>
            <div
              class="absolute bottom-0 left-0 w-32 h-32 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2"
            ></div>

            <div class="relative z-10">
              <h3 class="text-2xl font-bold mb-2">Contact Information</h3>
              <p class="text-white/80 text-sm mb-8">
                Fill out the form and we'll get back to you within 24 hours.
              </p>

              <!-- Email -->
              <a
                href="mailto:support@youthallynow.org"
                class="flex items-center gap-4 mb-6 group"
              >
                <div
                  class="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center group-hover:bg-white/20 transition-colors"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                <div>
                  <p class="text-white/60 text-xs uppercase tracking-wider">
                    Email
                  </p>
                  <p class="font-medium">support@youthallynow.org</p>
                </div>
              </a>

              <!-- Phone -->
              <a
                href="tel:+233249372583"
                class="flex items-center gap-4 mb-8 group"
              >
                <div
                  class="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center group-hover:bg-white/20 transition-colors"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                    />
                  </svg>
                </div>
                <div>
                  <p class="text-white/60 text-xs uppercase tracking-wider">
                    Phone
                  </p>
                  <p class="font-medium">(+233) 24 937 2583</p>
                </div>
              </a>

              <!-- Social Links -->
              <div class="flex gap-3">
                <a
                  href="https://web.facebook.com/youthallyorganisation"
                  target="_blank"
                  rel="noopener"
                  class="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                >
                  <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path
                      d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"
                    />
                  </svg>
                </a>
                <a
                  href="https://www.linkedin.com/in/youth-ally-youth"
                  target="_blank"
                  rel="noopener"
                  class="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                >
                  <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path
                      d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"
                    />
                  </svg>
                </a>
              </div>
            </div>
          </div>

          <!-- Office Hours Card -->
          <div
            class="bg-white rounded-2xl p-6 shadow-[0_10px_40px_-10px_rgba(0,0,0,0.15)] border border-gray-100 hover:shadow-[0_20px_50px_-10px_rgba(0,0,0,0.2)] transition-shadow duration-300"
          >
            <div class="flex items-center gap-3 mb-4">
              <div
                class="w-10 h-10 rounded-xl bg-logo-color/10 flex items-center justify-center"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-5 w-5 text-logo-color"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <h3 class="font-bold text-gray-900">Office Hours</h3>
            </div>
            <div class="space-y-3">
              <div
                class="flex justify-between items-center py-2 border-b border-gray-100"
              >
                <span class="text-gray-600 text-sm">Monday - Friday</span>
                <span
                  class="font-semibold text-gray-900 text-sm bg-logo-color/10 px-3 py-1 rounded-full"
                  >8:00 AM - 6:00 PM</span
                >
              </div>
              <div class="flex justify-between items-center py-2">
                <span class="text-gray-600 text-sm">Saturday - Sunday</span>
                <span class="font-semibold text-gray-500 text-sm">Closed</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Right Column - Form -->
        <div
          class="lg:col-span-3 bg-white rounded-3xl p-8 md:p-10 shadow-[0_15px_50px_-10px_rgba(0,0,0,0.15)] border border-gray-100 hover:shadow-[0_25px_60px_-10px_rgba(0,0,0,0.2)] transition-shadow duration-300"
          data-aos="fade-left"
          data-aos-duration="1000"
          data-aos-delay="200"
        >
          <div class="mb-8">
            <h2 class="text-2xl font-bold text-gray-900 mb-2">
              Send us a Message
            </h2>
            <p class="text-gray-500 text-sm">
              We're here to help and answer any questions you might have.
            </p>
          </div>

          <form @submit.prevent="handleSubmit" class="space-y-6">
            <div class="grid md:grid-cols-2 gap-5">
              <div class="form-group">
                <label class="form-label">Full Name</label>
                <input
                  v-model="name"
                  type="text"
                  class="form-input"
                  placeholder="Abena Asante"
                  required
                />
              </div>
              <div class="form-group">
                <label class="form-label">Email Address</label>
                <input
                  v-model="email"
                  type="email"
                  class="form-input"
                  placeholder="abena@example.com"
                  required
                />
              </div>
            </div>

            <div class="grid md:grid-cols-2 gap-5">
              <div class="form-group">
                <label class="form-label">Phone Number</label>
                <input
                  v-model="phone"
                  type="tel"
                  class="form-input"
                  placeholder="+233 24 937 2583"
                  required
                />
              </div>
              <div class="form-group">
                <label class="form-label">Subject</label>
                <input
                  v-model="subject"
                  type="text"
                  class="form-input"
                  placeholder="How can we help?"
                  required
                />
              </div>
            </div>

            <div class="form-group">
              <label class="form-label">Your Message</label>
              <textarea
                v-model="message"
                class="form-input min-h-[140px] resize-none"
                placeholder="Tell us more about your inquiry..."
                rows="5"
              ></textarea>
            </div>

            <button
              type="submit"
              :class="[
                'group w-full bg-logo-color text-white px-8 py-4 rounded-xl font-semibold transform transition-all duration-300 hover:shadow-lg hover:shadow-logo-color/25 flex items-center justify-center gap-3',
                isSubmitting
                  ? 'opacity-75 cursor-not-allowed'
                  : 'hover:-translate-y-0.5',
              ]"
              :disabled="isSubmitting"
            >
              <span v-if="isSubmitting" class="flex items-center gap-3">
                <svg
                  class="animate-spin h-5 w-5"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    class="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    stroke-width="4"
                  ></circle>
                  <path
                    class="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Sending Message...
              </span>
              <span v-else class="flex items-center gap-3">
                Send Message
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-5 w-5 group-hover:translate-x-1 transition-transform"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M14 5l7 7m0 0l-7 7m7-7H3"
                  />
                </svg>
              </span>
            </button>
          </form>
        </div>
      </div>
    </div>

    <!-- Success Modal -->
    <Transition name="modal">
      <div
        v-if="showSuccessModal"
        class="fixed inset-0 z-50 flex items-center justify-center p-4"
        @click="closeModal"
      >
        <div class="absolute inset-0 bg-gray-900/60 backdrop-blur-sm"></div>
        <div
          class="relative bg-white p-10 rounded-3xl shadow-2xl text-center max-w-md w-full transform"
          @click.stop
        >
          <!-- Decorative elements -->
          <div class="absolute -top-12 left-1/2 -translate-x-1/2">
            <div
              class="w-24 h-24 bg-logo-color rounded-full flex items-center justify-center shadow-lg shadow-logo-color/30"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-12 w-12 text-white"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
          </div>

          <div class="pt-8">
            <h2 class="text-2xl font-bold text-gray-900 mb-3">Message Sent!</h2>
            <p class="text-gray-600 mb-8">
              Thank you for reaching out. We've received your message and will
              get back to you within 24 hours.
            </p>
            <button
              @click="closeModal"
              class="w-full bg-logo-color hover:bg-logo-color/90 text-white px-8 py-3 rounded-xl font-semibold transform transition-all duration-300 hover:shadow-lg hover:shadow-logo-color/25"
            >
              Got it, thanks!
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script>
export default {
  name: "AskUs",
  data() {
    return {
      name: "",
      email: "",
      phone: "",
      subject: "",
      message: "",
      isSubmitting: false,
      showSuccessModal: false,
    };
  },
  methods: {
    async handleSubmit() {
      // Prevent multiple submissions
      if (this.isSubmitting) return;

      // Start submission process
      this.isSubmitting = true;

      try {
        const response = await fetch("https://formspree.io/f/", {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            name: this.name,
            email: this.email,
            phone: this.phone,
            subject: this.subject,
            message: this.message,
          }),
        });

        if (!response.ok) {
          throw new Error("Network response was not ok");
        }

        // Reset form and show success modal
        this.resetForm();
        this.showSuccessModal = true;
      } catch (error) {
        console.error("Submission failed:", error);
        // You might want to show an error modal or message here
      } finally {
        this.isSubmitting = false;
      }
    },
    resetForm() {
      this.name = "";
      this.email = "";
      this.phone = "";
      this.subject = "";
      this.message = "";
    },
    closeModal() {
      this.showSuccessModal = false;
    },
  },
};
</script>

<style scoped>
.form-group {
  @apply transition-all duration-300;
}

.form-label {
  @apply block text-gray-700 font-medium text-sm mb-2;
}

.form-input {
  @apply w-full px-4 py-3.5 border border-gray-200 rounded-xl focus:ring-4 focus:ring-logo-color/10
  focus:border-logo-color outline-none transition-all duration-300 bg-white text-gray-900 shadow-sm;
}

.form-input:hover {
  @apply border-gray-300 shadow-md;
}

.form-input:focus {
  @apply bg-white shadow-md shadow-logo-color/5;
}

.form-input::placeholder {
  @apply text-gray-400;
}

.form-group:focus-within .form-label {
  @apply text-logo-color;
}

/* Floating animation */
.animate-float {
  animation: float 6s ease-in-out infinite;
}

.animate-float-delayed {
  animation: float 6s ease-in-out infinite;
  animation-delay: 2s;
}

@keyframes float {
  0%,
  100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-20px);
  }
}

/* Modal transitions */
.modal-enter-active {
  transition: all 0.3s ease-out;
}

.modal-leave-active {
  transition: all 0.2s ease-in;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-from .relative,
.modal-leave-to .relative {
  transform: scale(0.95) translateY(20px);
}

.modal-enter-to .relative,
.modal-leave-from .relative {
  transform: scale(1) translateY(0);
}
</style>
